"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CLIENT_VISIBLE_REVISION_STATUSES = void 0;
exports.isClientVisibleRevisionStatus = isClientVisibleRevisionStatus;
exports.freezeRevisionReviewSnapshot = freezeRevisionReviewSnapshot;
exports.buildRevisionHistoryReview = buildRevisionHistoryReview;
exports.buildRevisionHistoryValueLine = buildRevisionHistoryValueLine;
exports.buildRevisionReviewView = buildRevisionReviewView;
const case_position_escrow_js_1 = require("../case-workflow/case-position-escrow.js");
const round_money_js_1 = require("../money/round-money.js");
const revision_discount_allocation_js_1 = require("./revision-discount-allocation.js");
const revision_diff_js_1 = require("./revision-diff.js");
const revision_gating_js_1 = require("./revision-gating.js");
const revision_live_snapshot_js_1 = require("./revision-live-snapshot.js");
const revision_adjustment_code_js_1 = require("./revision-adjustment-code.js");
exports.CLIENT_VISIBLE_REVISION_STATUSES = [
    "pending",
    "accepted",
    "declined",
    "withdrawn",
    "invalidated",
    "changes_requested",
];
function isClientVisibleRevisionStatus(status) {
    return exports.CLIENT_VISIBLE_REVISION_STATUSES.includes(status);
}
const DELETION_SENTENCE = "This milestone will be deleted from the project. The work will not be carried out and the client will not be charged for it.";
function calendarDaysDelta(from, to) {
    if (!from || !to || from === to)
        return null;
    const start = Date.parse(`${from}T00:00:00.000Z`);
    const end = Date.parse(`${to}T00:00:00.000Z`);
    if (Number.isNaN(start) || Number.isNaN(end))
        return null;
    return Math.round((end - start) / 86400000);
}
function invoiceCollected(invoice) {
    if (!invoice)
        return false;
    return invoice.status !== "pending" && invoice.status !== "draft";
}
function storedCaptured(invoice) {
    const value = invoice.milestoneAmountCaptured;
    if (value == null || !Number.isFinite(Number(value)))
        return null;
    return Number(value);
}
function deductionsForInvoice(invoiceId, adjustments, plans) {
    const attached = adjustments
        .filter((item) => item.invoiceId === invoiceId && item.status === "accepted" && item.kind === "reduction")
        .map((item) => ({ id: item.id, amount: Math.abs(Number(item.amount || 0)) }));
    // A discount split across invoices is not stored on one invoiceId. Each share is in the allocation plan.
    const shares = adjustments
        .filter((item) => item.status === "accepted" && item.kind === "reduction" && !item.invoiceId)
        .flatMap((item) => {
        const amount = (0, revision_discount_allocation_js_1.allocatedDiscountForInvoice)(plans, invoiceId, item.id);
        if (amount <= 0)
            return [];
        return [{ id: item.id, amount }];
    });
    return [...attached, ...shares];
}
function invoiceFunding(invoice, live) {
    const captured = storedCaptured(invoice);
    if (captured == null)
        return null;
    const milestones = live.milestones.filter((milestone) => milestone.projectInvoiceId === invoice.id || invoice.milestoneIds.includes(milestone.id));
    const additions = live.adjustments.filter((item) => item.invoiceId === invoice.id && item.status === "accepted" && item.kind === "addition");
    return {
        milestones,
        funding: (0, case_position_escrow_js_1.invoicePositionFunding)({
            captured,
            milestoneAmount: milestones.reduce((sum, milestone) => sum + Number(milestone.budgetAmount || 0), 0),
            additions: additions.map((item) => ({ id: item.id, amount: Number(item.amount || 0), createdAt: item.createdAt })),
            deductions: deductionsForInvoice(invoice.id, live.adjustments, live.adjustmentAllocations),
        }),
    };
}
function invoiceStillHoldsFundedEscrow(position, releasedAdjustmentIds) {
    const milestoneReleased = position.milestones.length > 0 && position.milestones.every((milestone) => milestone.moneyState === "fully_released");
    if (position.funding.milestone.funded > 0.009 && !milestoneReleased)
        return true;
    return position.funding.additions.some((row) => row.funded > 0.009 && !releasedAdjustmentIds?.has(row.id));
}
function adjustmentMoneyLabel(adjustment, live, releasedAdjustmentIds) {
    if (!adjustment.invoiceId)
        return null;
    const invoice = live.invoices.find((item) => item.id === adjustment.invoiceId);
    if (!invoice)
        return null;
    if (!invoiceCollected(invoice))
        return "pending";
    // A variation release is its own accepted request. Paying the invoice only funds it.
    if (adjustment.kind === "addition" && releasedAdjustmentIds?.has(adjustment.id))
        return "released";
    const position = invoiceFunding(invoice, live);
    // Without a stored capture, keep the paid-invoice label. Guessing would mark a legacy variation unfunded.
    if (!position)
        return "in_escrow";
    if (adjustment.kind === "addition") {
        const funded = position.funding.additions.find((row) => row.id === adjustment.id)?.funded ?? 0;
        if (funded <= 0.009)
            return "pending";
        return "in_escrow";
    }
    if (!invoiceStillHoldsFundedEscrow(position, releasedAdjustmentIds))
        return "deducted";
    return "in_escrow";
}
function currentContractValue(live) {
    const milestoneTotal = live.milestones.reduce((sum, milestone) => sum + Number(milestone.budgetAmount || 0), 0);
    const accepted = live.adjustments
        .filter((adjustment) => adjustment.status === "accepted")
        .reduce((sum, adjustment) => sum + (0, revision_adjustment_code_js_1.signedAdjustmentAmount)(adjustment), 0);
    return (0, round_money_js_1.roundMoney)(milestoneTotal + accepted);
}
function originalContractValue(live) {
    return (0, round_money_js_1.roundMoney)(live.milestones.reduce((sum, milestone) => sum + Number(milestone.budgetAmount || 0), 0));
}
function acceptedVariationsNet(live, meta) {
    const thisRevision = new Set(meta?.adjustmentIds ?? []);
    return (0, round_money_js_1.roundMoney)(live.adjustments
        .filter((adjustment) => adjustment.status === "accepted" && !thisRevision.has(adjustment.id))
        .reduce((sum, adjustment) => sum + (0, revision_adjustment_code_js_1.signedAdjustmentAmount)(adjustment), 0));
}
function thisRevisionValueNet(live, meta) {
    const added = meta.milestonesAdded.reduce((sum, milestone) => sum + Number(milestone.budgetAmount || 0), 0);
    const deleted = meta.milestonesDeleted.reduce((sum, item) => {
        const liveMilestone = live.milestones.find((milestone) => milestone.id === item.id);
        return sum + Number(liveMilestone?.budgetAmount || 0);
    }, 0);
    const adjustments = live.adjustments
        .filter((adjustment) => meta.adjustmentIds.includes(adjustment.id))
        .reduce((sum, adjustment) => sum + (0, revision_adjustment_code_js_1.signedAdjustmentAmount)(adjustment), 0);
    return (0, round_money_js_1.roundMoney)(added - deleted + adjustments);
}
function freezeRevisionReviewSnapshot(live, raw) {
    const liveSnapshot = JSON.parse(JSON.stringify(live));
    const view = buildRevisionReviewView(liveSnapshot, raw, liveSnapshot.adjustments);
    return {
        liveSnapshot,
        contractValueBefore: view.overview.beforeContractValue,
        contractValueAfter: view.overview.afterContractValue,
    };
}
function buildRevisionHistoryReview(live, raw, options) {
    const meta = (0, revision_diff_js_1.normalizeRevisionV2Metadata)(raw);
    const snapshot = (0, revision_live_snapshot_js_1.isLiveProjectSnapshot)(meta.liveSnapshot) ? meta.liveSnapshot : live;
    const view = buildRevisionReviewView(snapshot, meta, snapshot.adjustments, options);
    if (typeof meta.contractValueBefore === "number" && typeof meta.contractValueAfter === "number") {
        const beforeContractValue = (0, round_money_js_1.roundMoney)(meta.contractValueBefore);
        const afterContractValue = (0, round_money_js_1.roundMoney)(meta.contractValueAfter);
        const netChange = (0, round_money_js_1.roundMoney)(afterContractValue - beforeContractValue);
        view.overview = {
            ...view.overview,
            beforeContractValue,
            afterContractValue,
            netChange,
            noValueChange: netChange === 0,
            collapseToSingleLine: !view.overview.hasAcceptedAdjustments && netChange === 0,
        };
    }
    refreshAdjustmentMoneyLabels(view, live, options?.releasedAdjustmentIds);
    return view;
}
/** Money status follows the invoice now. The rest of the card stays the snapshot from send time. */
function refreshAdjustmentMoneyLabels(view, live, releasedAdjustmentIds) {
    for (const card of view.adjustments) {
        const current = live.adjustments.find((item) => item.id === card.id);
        if (!current?.invoiceId)
            continue;
        const label = adjustmentMoneyLabel(current, live, releasedAdjustmentIds);
        if (label)
            card.moneyLabel = label;
    }
}
function countPhrase(count, singular, plural) {
    return `${count} ${count === 1 ? singular : plural}`;
}
function overviewChangeLines(live, meta) {
    const lines = [];
    const addedAmount = (0, round_money_js_1.roundMoney)(meta.milestonesAdded.reduce((sum, milestone) => sum + Number(milestone.budgetAmount || 0), 0));
    if (meta.milestonesAdded.length) {
        lines.push({
            label: countPhrase(meta.milestonesAdded.length, "milestone added", "milestones added"),
            amount: addedAmount,
        });
    }
    if (meta.milestonesDeleted.length) {
        const deletedAmount = (0, round_money_js_1.roundMoney)(meta.milestonesDeleted.reduce((sum, item) => {
            const liveMilestone = live.milestones.find((milestone) => milestone.id === item.id);
            return sum + Number(liveMilestone?.budgetAmount || 0);
        }, 0));
        lines.push({
            label: countPhrase(meta.milestonesDeleted.length, "milestone deleted", "milestones deleted"),
            amount: (0, round_money_js_1.roundMoney)(-deletedAmount),
        });
    }
    const newAdjustments = live.adjustments.filter((adjustment) => meta.adjustmentIds.includes(adjustment.id));
    const variationOrders = newAdjustments.filter((adjustment) => adjustment.kind === "addition");
    const deductionOrders = newAdjustments.filter((adjustment) => adjustment.kind === "reduction");
    if (variationOrders.length) {
        lines.push({
            label: countPhrase(variationOrders.length, "variation order", "variation orders"),
            amount: (0, round_money_js_1.roundMoney)(variationOrders.reduce((sum, adjustment) => sum + (0, revision_adjustment_code_js_1.signedAdjustmentAmount)(adjustment), 0)),
        });
    }
    if (deductionOrders.length) {
        lines.push({
            label: countPhrase(deductionOrders.length, "deduction order", "deduction orders"),
            amount: (0, round_money_js_1.roundMoney)(deductionOrders.reduce((sum, adjustment) => sum + (0, revision_adjustment_code_js_1.signedAdjustmentAmount)(adjustment), 0)),
        });
    }
    return lines;
}
function collectedAmount(live) {
    const invoiceById = new Map(live.invoices.map((invoice) => [invoice.id, invoice]));
    const milestoneTotal = live.milestones.reduce((sum, milestone) => {
        const invoice = milestone.projectInvoiceId ? invoiceById.get(milestone.projectInvoiceId) : undefined;
        return invoiceCollected(invoice) ? sum + Number(milestone.budgetAmount || 0) : sum;
    }, 0);
    const adjustmentTotal = live.adjustments
        .filter((adjustment) => adjustment.status === "accepted")
        .reduce((sum, adjustment) => {
        const invoice = adjustment.invoiceId ? invoiceById.get(adjustment.invoiceId) : undefined;
        return invoiceCollected(invoice) ? sum + (0, revision_adjustment_code_js_1.signedAdjustmentAmount)(adjustment) : sum;
    }, 0);
    return (0, round_money_js_1.roundMoney)(milestoneTotal + adjustmentTotal);
}
function formatMoneyDelta(amount) {
    return Math.abs((0, round_money_js_1.roundMoney)(amount)).toFixed(2);
}
function buildRevisionHistoryValueLine(input) {
    const net = (0, round_money_js_1.roundMoney)(input.netChange);
    if (input.status === "changes_requested" || input.status === "withdrawn" || input.status === "invalidated" || input.status === "declined") {
        return null;
    }
    if (input.status === "pending") {
        if (net === 0)
            return "If accepted, no change to contract value";
        if (net > 0)
            return `If accepted, contract value increases by ${formatMoneyDelta(net)}`;
        return `If accepted, contract value reduces by ${formatMoneyDelta(net)}`;
    }
    if (input.status === "accepted") {
        if (net === 0)
            return input.voice === "client" ? "You accepted. No change to contract value" : "No change to contract value";
        if (net > 0) {
            return input.voice === "client"
                ? `You accepted. Contract value increased by ${formatMoneyDelta(net)}`
                : `Contract value increased by ${formatMoneyDelta(net)}`;
        }
        return input.voice === "client"
            ? `You accepted. Contract value reduced by ${formatMoneyDelta(net)}`
            : `Contract value reduced by ${formatMoneyDelta(net)}`;
    }
    return null;
}
function buildMilestoneCard(live, meta, deleted) {
    const change = meta.milestoneChanges.find((item) => item.id === live.id);
    const nextName = change?.fields.name ?? live.name;
    const nextDescription = change?.fields.description === undefined ? (live.description ?? "") : (change.fields.description ?? "");
    const nextStart = (0, revision_gating_js_1.toDateOnly)(change?.fields.startDate !== undefined ? change.fields.startDate : live.startDate);
    const nextEnd = (0, revision_gating_js_1.toDateOnly)(change?.fields.endDate !== undefined ? change.fields.endDate : live.endDate);
    const prevStart = (0, revision_gating_js_1.toDateOnly)(live.startDate);
    const prevEnd = (0, revision_gating_js_1.toDateOnly)(live.endDate);
    const nameChanged = Boolean(change?.fields.name && change.fields.name !== live.name);
    const descriptionChanged = change?.fields.description !== undefined && (change.fields.description ?? "") !== (live.description ?? "");
    const startChanged = Boolean(change?.fields.startDate !== undefined && nextStart !== prevStart);
    const endChanged = Boolean(change?.fields.endDate !== undefined && nextEnd !== prevEnd);
    const fieldChanged = nameChanged || descriptionChanged || startChanged || endChanged;
    const deletedSubs = new Set(meta.submilestonesDeleted.filter((item) => item.parentMilestoneId === live.id).map((item) => item.id));
    const subChanges = meta.submilestoneChanges.filter((item) => item.parentMilestoneId === live.id);
    const submilestones = [
        ...(live.subMilestones ?? []).map((sub) => {
            const subChange = subChanges.find((item) => item.id === sub.id);
            const isDeleted = deletedSubs.has(sub.id);
            const name = subChange?.fields.name ?? sub.name;
            const changed = Boolean(subChange) && !isDeleted;
            return {
                id: sub.id,
                tag: (isDeleted ? "deleted" : changed ? "changed" : null),
                name,
                description: subChange?.fields.description === undefined ? (sub.description ?? "") : (subChange.fields.description ?? ""),
                amount: subChange?.fields.amount ?? sub.budgetAmount,
                startDate: (0, revision_gating_js_1.toDateOnly)(subChange?.fields.startDate !== undefined ? subChange.fields.startDate : sub.startDate),
                endDate: (0, revision_gating_js_1.toDateOnly)(subChange?.fields.endDate !== undefined ? subChange.fields.endDate : sub.endDate),
            };
        }),
        ...meta.submilestonesAdded
            .filter((item) => item.parentMilestoneId === live.id)
            .map((added) => ({
            id: added.tempId,
            tag: "new",
            name: added.name,
            description: added.description ?? "",
            amount: added.amount,
            startDate: (0, revision_gating_js_1.toDateOnly)(added.startDate),
            endDate: (0, revision_gating_js_1.toDateOnly)(added.endDate),
        })),
    ];
    return {
        id: live.id,
        tag: deleted ? "deleted" : fieldChanged ? "changed" : null,
        name: deleted ? live.name : nextName,
        previousName: nameChanged ? live.name : undefined,
        description: deleted ? (live.description ?? "") : nextDescription,
        previousDescription: descriptionChanged ? (live.description ?? "") : undefined,
        amount: Number(live.budgetAmount || 0),
        startDate: deleted ? prevStart : nextStart,
        previousStartDate: startChanged ? prevStart : undefined,
        endDate: deleted ? prevEnd : nextEnd,
        previousEndDate: endChanged ? prevEnd : undefined,
        startPushedDays: deleted ? null : calendarDaysDelta(prevStart, nextStart),
        endPushedDays: deleted ? null : calendarDaysDelta(prevEnd, nextEnd),
        moneyLabel: deleted ? null : (0, revision_gating_js_1.mapMoneyStateToDisplayLabel)(live.moneyState),
        deletionSentence: deleted ? DELETION_SENTENCE : undefined,
        deletionStripAmount: deleted ? Number(live.budgetAmount || 0) : undefined,
        submilestones,
    };
}
function buildAddedMilestoneCard(added) {
    return {
        id: added.tempId,
        tag: "new",
        name: added.name,
        description: added.description ?? "",
        amount: Number(added.budgetAmount || 0),
        startDate: (0, revision_gating_js_1.toDateOnly)(added.startDate),
        endDate: (0, revision_gating_js_1.toDateOnly)(added.endDate),
        moneyLabel: "pending",
        submilestones: [],
    };
}
function issuedInvoiceLabel(tag, invoiceCode) {
    if (tag === "new" || !invoiceCode)
        return "Not yet issued";
    return invoiceCode;
}
function idsChanged(previous, next) {
    return [...(previous ?? [])].sort().join(",") !== [...(next ?? [])].sort().join(",");
}
function resolvedInvoiceAssignments(live, meta) {
    const deletedIds = new Set(meta.invoiceChanges.filter((change) => change.delete && change.id).map((change) => change.id));
    const existing = live.invoices.map((invoice) => {
        const change = meta.invoiceChanges.find((item) => item.id === invoice.id && !item.delete);
        const milestoneIds = change?.milestoneIds ?? invoice.milestoneIds;
        const adjustmentIds = change?.adjustmentIds ?? invoice.adjustmentIds ?? [];
        const tag = deletedIds.has(invoice.id) ? "deleted" : null;
        return {
            id: invoice.id,
            tag,
            dueDate: (0, revision_gating_js_1.toDateOnly)(change?.dueDate !== undefined ? change.dueDate : invoice.dueDate),
            previousDueDate: (0, revision_gating_js_1.toDateOnly)(invoice.dueDate),
            issuedLabel: issuedInvoiceLabel(tag, invoice.invoiceCode),
            assignmentChanged: idsChanged(invoice.milestoneIds, milestoneIds) || idsChanged(invoice.adjustmentIds, adjustmentIds),
            milestoneIds,
            adjustmentIds,
        };
    });
    const created = meta.invoiceChanges
        .filter((change) => change.isNew || (!change.id && change.tempId))
        .map((change) => ({
        id: change.tempId || change.id || `new-${change.dueDate ?? "invoice"}`,
        tag: "new",
        dueDate: (0, revision_gating_js_1.toDateOnly)(change.dueDate),
        previousDueDate: null,
        issuedLabel: "Not yet issued",
        assignmentChanged: true,
        milestoneIds: change.milestoneIds ?? [],
        adjustmentIds: change.adjustmentIds ?? [],
    }));
    return [...existing, ...created];
}
function filterChangedOnly(view) {
    return {
        ...view,
        changedOnly: true,
        milestones: view.milestones.filter((milestone) => milestone.tag || milestone.submilestones.some((sub) => sub.tag)),
        adjustments: view.adjustments.filter((adjustment) => adjustment.tag),
        invoices: view.invoices.filter((invoice) => invoice.tag || invoice.previousDueDate || invoice.assignmentChanged),
    };
}
function buildRevisionReviewView(live, raw, adjustments = live.adjustments, options) {
    const snapshot = { ...live, adjustments };
    const meta = (0, revision_diff_js_1.normalizeRevisionV2Metadata)(raw);
    const plan = (0, revision_diff_js_1.applyRevisionDiff)(snapshot, meta);
    const originalValue = originalContractValue(snapshot);
    const acceptedNet = acceptedVariationsNet(snapshot, meta);
    let beforeContractValue = currentContractValue(snapshot);
    let afterContractValue = plan.contractValue;
    let netChange = (0, round_money_js_1.roundMoney)(afterContractValue - beforeContractValue);
    const frozenLive = (0, revision_live_snapshot_js_1.isLiveProjectSnapshot)(meta.liveSnapshot);
    if (!frozenLive) {
        const metadataNet = thisRevisionValueNet(snapshot, meta);
        if (metadataNet !== netChange) {
            afterContractValue = currentContractValue(snapshot);
            netChange = metadataNet;
            beforeContractValue = (0, round_money_js_1.roundMoney)(afterContractValue - netChange);
        }
    }
    const hasAcceptedAdjustments = snapshot.adjustments.some((item) => item.status === "accepted" && !meta.adjustmentIds.includes(item.id));
    const noValueChange = netChange === 0;
    const changeLines = overviewChangeLines(snapshot, meta);
    const deletedIds = new Set(meta.milestonesDeleted.map((item) => item.id));
    const liveById = new Map(snapshot.milestones.map((milestone) => [milestone.id, milestone]));
    const milestones = [
        ...snapshot.milestones.map((milestone) => buildMilestoneCard(milestone, meta, deletedIds.has(milestone.id))),
        ...meta.milestonesAdded.map(buildAddedMilestoneCard),
    ];
    const newAdjustmentIds = new Set(meta.adjustmentIds);
    const referenced = snapshot.adjustments.filter((item) => meta.adjustmentIds.includes(item.id) || item.status === "accepted");
    const adjustmentsView = [
        ...referenced.filter((item) => newAdjustmentIds.has(item.id)),
        ...referenced.filter((item) => !newAdjustmentIds.has(item.id)),
    ].map((item) => ({
        id: item.id,
        tag: newAdjustmentIds.has(item.id) ? "new" : null,
        kind: item.kind,
        label: (0, revision_adjustment_code_js_1.adjustmentKindLabel)(item.kind),
        displayCode: item.displayCode ?? null,
        amount: Math.abs(Number(item.amount || 0)),
        signedAmount: (0, revision_adjustment_code_js_1.signedAdjustmentAmount)(item),
        description: item.description ?? "",
        moneyLabel: adjustmentMoneyLabel(item, snapshot, options?.releasedAdjustmentIds),
    }));
    const nameByMilestoneId = new Map(milestones.map((card) => [card.id, card]));
    const invoices = resolvedInvoiceAssignments(snapshot, meta).map((invoice) => {
        const lines = [
            ...invoice.milestoneIds.map((id) => {
                const card = nameByMilestoneId.get(id);
                const liveMilestone = liveById.get(id);
                return {
                    kind: "milestone",
                    id,
                    name: card?.name ?? liveMilestone?.name ?? "Milestone",
                    amount: card?.amount ?? Number(liveMilestone?.budgetAmount || 0),
                };
            }),
            ...invoice.adjustmentIds.map((id) => {
                const card = adjustmentsView.find((item) => item.id === id);
                const liveAdj = snapshot.adjustments.find((item) => item.id === id);
                return {
                    kind: "adjustment",
                    id,
                    name: card?.description || (0, revision_adjustment_code_js_1.adjustmentDisplayTitle)(liveAdj?.kind ?? "addition", liveAdj?.displayCode),
                    amount: card?.signedAmount ?? (liveAdj ? (0, revision_adjustment_code_js_1.signedAdjustmentAmount)(liveAdj) : 0),
                };
            }),
        ];
        const dueChanged = invoice.tag !== "new" && invoice.dueDate !== invoice.previousDueDate;
        return {
            id: invoice.id,
            tag: invoice.tag,
            dueDate: invoice.dueDate,
            previousDueDate: dueChanged ? invoice.previousDueDate : undefined,
            issuedLabel: invoice.issuedLabel,
            assignmentChanged: invoice.assignmentChanged,
            lines,
        };
    });
    const nextStart = (0, revision_gating_js_1.toDateOnly)(meta.projectStartDate ?? snapshot.startDate);
    const nextEnd = (0, revision_gating_js_1.toDateOnly)(meta.projectEndDate ?? snapshot.endDate);
    const prevStart = (0, revision_gating_js_1.toDateOnly)(snapshot.startDate);
    const prevEnd = (0, revision_gating_js_1.toDateOnly)(snapshot.endDate);
    const view = {
        overview: {
            originalContractValue: originalValue,
            acceptedVariationsNet: acceptedNet,
            beforeContractValue,
            afterContractValue,
            netChange,
            noValueChange,
            hasAcceptedAdjustments,
            collapseToSingleLine: !hasAcceptedAdjustments && noValueChange,
            changeLines,
        },
        projectDates: {
            startDate: nextStart,
            endDate: nextEnd,
            previousStartDate: nextStart !== prevStart ? prevStart : undefined,
            previousEndDate: nextEnd !== prevEnd ? prevEnd : undefined,
            startPushedDays: calendarDaysDelta(prevStart, nextStart),
            endPushedDays: calendarDaysDelta(prevEnd, nextEnd),
        },
        milestones,
        adjustments: adjustmentsView,
        invoices,
        remainingToPay: (0, round_money_js_1.roundMoney)(Math.max(0, afterContractValue - collectedAmount(snapshot))),
        noteToClient: meta.noteToClient,
        rounds: meta.rounds,
    };
    return options?.changedOnly ? filterChangedOnly(view) : view;
}
